import React from 'react';
import { CheckCircle, Globe, ArrowRight, BarChart3, MapPin, Users, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

const DestinationAnalytics: React.FC = () => {
    const navigate = useNavigate();

    const handleActionClick = () => {
        navigate('/eligibility?service=admissions');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <SEO
                title="Global Destination Analytics | NexOverseas"
                description="Mathematically assess the best global destinations for your education and career. Compare historical visa trends, post-study work outcomes, and ROI."
                canonical="/admissions/destination-analytics"
            />

            <div className="bg-[#011E26] text-white py-24 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[#009B8F]/20"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
                    <span className="text-[#009B8F] font-bold tracking-widest text-xs uppercase mb-4 block">Admissions Suite</span>
                    <h1 className="text-4xl md:text-6xl font-serif mb-6">Global Destination Analytics</h1>
                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        Assess the best global destinations for your education and career through comparative data.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start mb-24">
                    <div className="prose prose-lg text-slate-600 max-w-none">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-[#009B8F]/10 rounded-full">
                                <Globe className="text-[#009B8F] w-8 h-8" />
                            </div>
                            <h2 className="text-3xl font-serif text-[#011E26] m-0">The Mathematics of Choice</h2>
                        </div>

                        <p className="leading-relaxed mb-6">
                            Selecting a study destination involves more than choosing a country; it’s a long‑term investment
                            decision. Political shifts, changing visa regulations, and fluctuating economic conditions
                            can significantly impact the return on your international education.
                        </p>

                        <p className="leading-relaxed mb-6">
                            NexOverseas provides Global Destination Analytics to help students mathematically assess
                            which country offers the best trajectory for their specific goals. The platform compares
                            historical visa approval trends, post‑study work regulations, and average graduate salaries
                            across different regions.
                        </p>

                        <p className="leading-relaxed mb-8">
                            By centralising complex global data into one accessible dashboard, we convert subjective
                            perceptions into objective analytics. We ensure students select destinations not just
                            based on popularity, but on real outcomes and future career security.
                        </p>

                        <button
                            onClick={handleActionClick}
                            className="group inline-flex items-center gap-3 bg-[#009B8F] text-white px-10 py-5 font-bold uppercase tracking-widest text-xs hover:bg-[#011E26] transition-all duration-300 rounded-sm shadow-2xl"
                        >
                            Run a check with AI <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            { icon: <TrendingUp className="text-[#009B8F]" />, title: "Visa Approval Trends", desc: "Access real-time data on visa success rates across UK, USA, Canada, and Australia." },
                            { icon: <Users className="text-[#009B8F]" />, title: "Labour Market Insight", desc: "Identify countries with skill shortages aligned with your chosen field of study." },
                            { icon: <MapPin className="text-[#009B8F]" />, title: "Regional ROI Comparison", desc: "Compare average cost of living vs. projected graduate salaries in multiple cities." },
                            { icon: <BarChart3 className="text-[#009B8F]" />, title: "Post-Study Work Paths", desc: "Visualise timelines for work permits and long-term residency pathways." }
                        ].map((card, i) => (
                            <div key={i} className="bg-white p-8 rounded-sm border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="mb-4">{card.icon}</div>
                                <h4 className="font-bold text-[#011E26] text-sm uppercase tracking-wider mb-2">{card.title}</h4>
                                <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-20 mb-20">
                    <h3 className="text-3xl font-serif text-[#011E26] mb-12 text-center">Supported Global Destinations</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center">
                        {[
                            { name: "United Kingdom", img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&q=80&w=200" },
                            { name: "United States", img: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?auto=format&fit=crop&q=80&w=200" },
                            { name: "Canada", img: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&q=80&w=200" },
                            { name: "Australia", img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&q=80&w=200" },
                            { name: "New Zealand", img: "https://images.unsplash.com/photo-1589330273594-fade1ee91647?auto=format&fit=crop&q=80&w=200" },
                            { name: "Malaysia", img: "https://images.unsplash.com/photo-1621330396173-e41b1cafd17f?auto=format&fit=crop&q=80&w=200" }
                        ].map((dest, i) => (
                            <div key={i} className="group cursor-default">
                                <div className="relative w-24 h-24 mx-auto mb-4 rounded-sm overflow-hidden border-2 border-slate-100 group-hover:border-[#009B8F] transition-colors">
                                    <img src={dest.img} alt={dest.name} className="w-full h-full object-cover" />
                                </div>
                                <p className="text-xs font-bold uppercase tracking-widest text-slate-600">{dest.name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationAnalytics;
